import React, { useState } from "react";
import { Loader2, Save, X, ImagePlus } from "lucide-react";
import { resolveAssetUrl } from "../../lib/api";
import { GALLERY_CATEGORIES } from "../../hooks/useGallery";
import MediaPicker from "./MediaPicker";

const Label = ({ children }) => (
  <label className="font-display text-[10px] uppercase tracking-[0.3em] text-[#0A192F]/70 font-semibold">
    {children}
  </label>
);

export const GalleryEditModal = ({ item, onChange, onSave, onClose }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave();
    setSaving(false);
    if (ok) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      data-testid="admin-gallery-editor-modal"
    >
      <div className="bg-white w-full max-w-lg my-8 sm:my-0">
        <div className="bg-[#0A192F] text-white px-6 py-4 flex items-center justify-between">
          <h2 className="font-display font-bold uppercase tracking-widest text-sm">
            {item.id ? "Edit Photo" : "Add Photo"}
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <Label>Image URL</Label>
            <div className="flex gap-2 mt-2">
              <input
                value={item.url}
                onChange={(e) => onChange({ ...item, url: e.target.value })}
                placeholder="Paste image URL or click Browse"
                data-testid="admin-gallery-input-url"
                className="flex-1 bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
              />
              <button
                onClick={() => setPickerOpen(true)}
                data-testid="admin-gallery-browse-media"
                className="inline-flex items-center gap-1 border border-gray-300 px-3 text-xs font-display uppercase tracking-wider hover:border-[#D4AF37]"
              >
                <ImagePlus size={14} /> Browse
              </button>
            </div>
          </div>
          {item.url && (
            <div className="aspect-video bg-gray-100 overflow-hidden">
              <img
                src={resolveAssetUrl(item.url)}
                alt="preview"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div>
            <Label>Label / Caption</Label>
            <input
              value={item.label}
              onChange={(e) => onChange({ ...item, label: e.target.value })}
              data-testid="admin-gallery-input-label"
              className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
            />
          </div>
          <div>
            <Label>Category</Label>
            <select
              value={item.category}
              onChange={(e) => onChange({ ...item, category: e.target.value })}
              data-testid="admin-gallery-input-category"
              className="mt-2 w-full bg-white border border-gray-300 px-4 py-3 text-[#0A192F] focus:border-[#D4AF37] focus:outline-none"
            >
              {GALLERY_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-5 py-2.5 font-display text-xs uppercase tracking-wider text-[#0A192F]/70 hover:text-[#0A192F]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="admin-gallery-save"
            className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-2.5 font-display text-xs uppercase tracking-wider font-bold hover:bg-[#D4AF37] hover:text-[#0A192F] disabled:opacity-60"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            Save
          </button>
        </div>
      </div>

      {pickerOpen && (
        <MediaPicker
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => {
            onChange({ ...item, url });
            setPickerOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default GalleryEditModal;
